'use client';

import Image from 'next/image';
import { Button, Modal, RadioButton, RadioGroup } from '@AydiTeam1/aydi-ui';
import { useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { LangCode, useChangeLang, useLangs } from '../hooks/useChangeLang';
import { useUpdateUserConfig } from '@/modules/setting/api';
import { useBoundStore } from '../stores/store';
import { useUserProfile } from '../api';
import useOptimisticUpdate from '../hooks/useOptimisticUpdate';
import { User } from '@/modules/authentication/types';
import { useSearchParams } from 'next/navigation';
import { useRouter } from '@/i18n/navigation';

export default function LanguageSwitch() {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const accessToken = useBoundStore((s) => s.accessToken);
  const router = useRouter();
  const { data: profile } = useUserProfile(!!accessToken);
  const { mutateAsync: updateUserConfig, isPending } = useUpdateUserConfig(
    profile?.id
  );
  const LANGS = useLangs();

  const currentLocale = useLocale();

  const [selectedIndex, setSelectedIndex] = useState(() =>
    LANGS.findIndex((l) => l.code === currentLocale)
  );
  const [tempSelectedIndex, setTempSelectedIndex] = useState(() =>
    LANGS.findIndex((l) => l.code === currentLocale)
  );
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const changeLanguage = useChangeLang();

  // Sync in invitation Flow
  const lang = decodeURIComponent(searchParams.get('lang') || '');
  useEffect(() => {
    if (lang) {
      document.cookie = `locale=${lang}; path=/; max-age=31536000`; // 1 year expiry
      // remove the lang param from the search params
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('lang');
      router.replace(`${window.location.pathname}?${newSearchParams.toString()}`);
      router.refresh();
      setTempSelectedIndex(LANGS.findIndex((l) => l.code === lang));
      setSelectedIndex(LANGS.findIndex((l) => l.code === lang));
    }
  }, [lang, , changeLanguage]);


  const { run: runOptimistic } = useOptimisticUpdate<User>();
  const updateOnServer = async () => {
    const locale = LANGS[tempSelectedIndex].code as LangCode;
    changeLanguage(locale);
    await runOptimistic({
      queryKey: { queryKey: ['userProfile'], exact: true },
      makeOptimistic: (prev) => {
        setSelectedIndex(tempSelectedIndex);
        setIsOpen(false);
        return {
          ...prev,
          userConfig: {
            ...prev.userConfig,
            language: locale
          }
        }
      },
      mutation: async () => {
        return await updateUserConfig({
          body: {
            language: locale,
          },
        });
      },
      invalidate: [
        { queryKey: ['userProfile'], exact: true },
        { queryKey: ['ALL_CROPS']},
        { queryKey: ['IDEAL_CUSTOMER_PROFILES']},
      ],
      onErrorMessage: 'Failed to update profile',
    });
  };

  const updateOnlyLocally = () => {
    const locale = LANGS[tempSelectedIndex].code as LangCode;
    changeLanguage(locale);
    setSelectedIndex(tempSelectedIndex);
    setIsOpen(false);
  };

  const onSaveClicked = () => {
    if (accessToken) {
      updateOnServer();
    } else {
      updateOnlyLocally();
    }
  };

  const onCancelClicked = () => {
    setIsOpen(false);
    setTempSelectedIndex(selectedIndex);
  };

  // aydi-ui's Modal uses a useMediaQuery whose server snapshot throws
  // ("useMediaQuery is a client-only hook"), which 500s every page that renders
  // it. Mount after hydration — this switch has no meaningful server output.
  if (!mounted) return null;

  return (
    <Modal
      actionTrigger={
        <button
          onClick={() => setIsOpen(true)}
          aria-label='Change language'
          className='inline-flex h-[24px] w-[24px] items-center justify-center overflow-hidden rounded-full bg-background ring-1 ring-black/5'
        >
          {/* circular trigger flag */}
          <span className='relative h-full w-full'>
            <Image
              src={LANGS[selectedIndex].flag.src}
              alt={LANGS[selectedIndex].label}
              fill
              className='object-cover'
              sizes='24px'
              priority
            />
          </span>
        </button>
      }
      dismissible={false}
      dialogSize='fit'
      open={isOpen}
      type='dialog'
      onOpenChange={() => { }}
    >
      <div className='text-on-background'>
        <h2 className='mb-6 text-[24px] font-semibold leading-tight'>
          {t('actions.chooseLanguage')}
        </h2>

        <RadioGroup>
          <div className='space-y-[16px] mb-[40px]'>
            {LANGS.map(({ code, label, flag }, index) => {
              const isActive = tempSelectedIndex === index;
              return (
                <div
                  key={code}
                  onClick={() => setTempSelectedIndex(index)}
                  className={[
                    'w-full h-[60px] rounded-[28px] border px-5 transition cursor-pointer',
                    isActive
                      ? 'border-primary  border-2 bg-primary-container'
                      : 'border-transparent bg-on-primary ',
                  ].join(' ')}
                >
                  <div className='flex h-full items-center justify-between gap-16 '>
                    <div className='flex items-center gap-4'>
                      {/* circular list flag (selected & unselected) */}
                      <span className='relative h-40 w-40 overflow-hidden rounded-full ring-1 ring-black/10 bg-background'>
                        <Image
                          src={flag.src}
                          alt=''
                          fill
                          className='object-cover'
                          sizes='40px'
                          priority={isActive}
                        />
                      </span>
                      <span className='text-[18px] font-semibold'>{label}</span>
                    </div>

                    <RadioButton
                      id={code}
                      value={code}
                      checked={isActive}
                      onClick={() => setTempSelectedIndex(index)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </RadioGroup>

        <div className='mt-10 flex items-center justify-end gap-3'>
          <Button
            variant='ghost'
            disabled={isPending}
            className='rounded-xl px-4 py-2 h-[40px] text-[15px] font-medium text-slate-700 hover:bg-slate-100'
            onClick={onCancelClicked}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            className='h-[40px] '
            disabled={isPending}
            isLoading={isPending}
            onClick={onSaveClicked}
          >
            {t('actions.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
